import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import PageHero from '../components/PageHero';
import ServiceSection from '../components/ServiceSection';
import ContactForm from '../components/ContactForm';
import Footer from '../components/Footer';
import ServiceSchema from '../components/ServiceSchema';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Helmet } from "react-helmet";

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const breadcrumbItems = [
    { label: 'HOME', path: '/' },
    { label: 'SERVICES', path: '/services' },
    { label: service.title.toUpperCase() }
  ];

  return (
    <div className="page-wrapper">
      <ServiceSchema service={service} />
      <BreadcrumbSchema items={breadcrumbItems} />
      
      <PageHero 
        title={service.title}
        breadcrumbItems={breadcrumbItems}
        subtitle={service.description}
      />
      
      {/* Overview Section */}
      <ServiceSection title="Overview" className="bg-primary">
        <div className="service-overview-layout">
          <div className="service-overview-image">
            <img src={service.image} alt={service.title} />
            <div className="service-overview-overlay"></div>
          </div>
          <div className="service-overview-text">
            {service.overview.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="overview-paragraph">{paragraph}</p>
            ))}
          </div>
        </div>
      </ServiceSection>

      {/* Use Cases Section */}
      <ServiceSection title="Use Cases" className="bg-secondary">
        <div className="use-cases-grid">
          {service.useCases.map((useCase, idx) => (
            <div key={idx} className="use-case-card">
              <CheckCircle2 className="use-case-icon" />
              <p>{useCase}</p>
            </div>
          ))}
        </div>
      </ServiceSection>

      {/* Materials Supported Section */}
      <ServiceSection title="Materials Supported" className="bg-primary">
        {service.materialsGroups ? (
          <div className="materials-groups-list" data-testid={`service-materials-groups-${service.slug}`}>
            {service.materialsGroups.map((group, groupIdx) => (
              <div key={`${group.title}-${groupIdx}`} className="material-group-card" data-testid={`service-material-group-${service.slug}-${groupIdx}`}>
                <h3 className="material-group-title">{group.title}</h3>
                <ul className="material-group-items">
                  {group.items.map((item, itemIdx) => (
                    <li key={`${item.name}-${itemIdx}`} className="material-group-item" data-testid={`service-material-item-${service.slug}-${groupIdx}-${itemIdx}`}>
                      <CheckCircle2 className="material-group-icon" />
                      <span>
                        <strong>{item.name}</strong>
                        {item.description ? ` (${item.description})` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="materials-list" data-testid={`service-materials-list-${service.slug}`}>
            {Object.entries(service.materialsDetailed).map(([material, description]) => (
              <div key={material} className="material-item" data-testid={`service-material-item-${service.slug}-${material.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <h3 className="material-name">{material}</h3>
                <p className="material-description">{description}</p>
              </div>
            ))}
          </div>
        )}
      </ServiceSection>

      {/* Ideal Industries Section */}
      <ServiceSection title="Ideal Industries" className="bg-secondary">
        <div className="industries-tags-grid">
          {service.industries.map((industry, idx) => (
            <div key={idx} className="industry-tag-large">
              {industry}
            </div>
          ))}
        </div>
      </ServiceSection>

      {/* Technical Strengths Section */}
      <ServiceSection title="Technical Strengths" className="bg-primary">
        <div className="strengths-list">
          {service.strengths.map((strength, idx) => (
            <div key={idx} className="strength-item">
              <CheckCircle2 className="strength-icon" />
              <span>{strength}</span>
            </div>
          ))}
        </div>
      </ServiceSection>

      {/* Process Section (if available) */}
      {service.processSteps && (
        <ServiceSection title="Our Process" className="bg-secondary">
          <div className="service-process-flow">
            {service.processSteps.map((step, idx) => (
              <div key={idx} className="process-flow-item">
                <div className="process-flow-number">{String(idx + 1).padStart(2, '0')}</div>
                <p className="process-flow-text">{step}</p>
                {idx < service.processSteps.length - 1 && (
                  <div className="process-flow-arrow">→</div>
                )}
              </div>
            ))}
          </div>
        </ServiceSection>
      )}

      {/* CTA Section */}
      <section className="service-cta-section">
        <div className="container-modern">
          <div className="service-cta-content">
            <h2 className="service-cta-title">
              {service.ctaTitle || `Ready to Get Started with ${service.title}?`}
            </h2>
            <p className="service-cta-text">
              {service.ctaText || "Get a quote today and let our engineering team help bring your project to life with precision and reliability."}
            </p>
            <div className="service-cta-buttons">
              <Button className="primary-cta large-cta" onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) contactSection.scrollIntoView({ behavior: 'smooth' });
              }}>
                Request a Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button className="secondary-cta large-cta" onClick={() => window.open(`https://wa.me/+919537275691`, '_blank')}>
                Chat on WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
